import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, Button } from '@ideoz/ui';
import { fixFilenameMojibake } from '@ideoz/core';
import { UploadFilesDialog } from './components/UploadFilesDialog';
import { GOOGLE_AUTH_CONFIG } from '../../config/googleAuth';
import type { FilesOwner } from './files.types';
import { useFiles } from './useFiles';
import { FilesHeader } from './components/FilesHeader';
import { FilesList } from './components/FilesList';
import { FilesEmptyState } from './components/FilesEmptyState';

interface FilesModuleProps {
  owner: FilesOwner;
}

/**
 * Files feature module. Owns no owner-specific branching beyond the `owner`
 * prop it forwards to `useFiles` and the copy lookup — the same component
 * renders for a startup and for a scope.
 */
export function FilesModule({ owner }: FilesModuleProps): JSX.Element {
  const { attachments, isLoading, deletingId, fetchAttachments, uploadFiles, deleteFile } =
    useFiles(owner);
  const { t } = useTranslation();
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  // Delete goes through a confirmation: the trash button opens this, the actual
  // delete only runs on confirm. Holds the attachment id, or null when closed.
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);
  const description = t(owner.type === 'scope' ? 'filesHub.descScope' : 'filesHub.descStartup');

  const pendingAttachment = fileToDelete
    ? attachments.find((a) => a.id === fileToDelete)
    : undefined;
  const pendingName = pendingAttachment
    ? fixFilenameMojibake(
        pendingAttachment.fileName ??
          pendingAttachment.name ??
          pendingAttachment.originalName ??
          'this file'
      )
    : 'this file';

  return (
    <section className="mx-auto w-full max-w-5xl px-6 py-10">
      <FilesHeader description={description} onUpload={() => setIsUploadOpen(true)} />

      <div className="bg-surface rounded-2xl border border-gray-100 dark:bg-gray-800 dark:border-gray-700">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-gray-300 dark:text-gray-500 animate-spin" />
          </div>
        ) : attachments.length === 0 ? (
          <FilesEmptyState onUpload={() => setIsUploadOpen(true)} />
        ) : (
          <FilesList attachments={attachments} deletingId={deletingId} onDelete={setFileToDelete} />
        )}
      </div>

      <UploadFilesDialog
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        projectAttachments={attachments as any}
        onFilesUpload={uploadFiles}
        onFileDelete={deleteFile}
        onFetchAttachments={fetchAttachments}
        isWeb
        googleAuthConfig={GOOGLE_AUTH_CONFIG}
        projectId={owner.startupId || undefined}
      />

      {/* Delete confirmation */}
      <Dialog
        open={!!fileToDelete}
        onOpenChange={(open) => {
          if (!open) setFileToDelete(null);
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('filesHub.deleteTitle')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 dark:text-gray-300 break-all">
            {t('filesHub.deleteBody', { name: pendingName })}
          </p>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setFileToDelete(null)}>
              {t('filesHub.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={!!deletingId}
              onClick={() => {
                const id = fileToDelete;
                setFileToDelete(null);
                if (id) void deleteFile(id);
              }}
            >
              {t('filesHub.delete')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
